import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMXN, statusColor, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, FileText, Users, BookOpen, CheckCircle2, Sprout } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const StatBlock = ({ label, value, sub, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className="border border-border bg-white p-6 rounded-lg flex flex-col gap-3 hover-lift"
  >
    <div className="flex items-center justify-between">
      <span className="uppercase-label">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-[#8B5A33]" strokeWidth={1.5} />}
    </div>
    <div className="font-display text-4xl text-foreground tabular">{value}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [cxc, setCxc] = useState({ items: [], total_cobrado: 0, total_pendiente: 0 });
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/stats"), api.get("/cuentas-por-cobrar")]);
      setStats(s.data);
      setCxc(c.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/seed");
      if (data.seeded) {
        toast.success(`Catálogo iniciado con ${data.items} partidas`);
      } else {
        toast.info(data.message || "Ya existe data");
      }
      load();
    } catch {
      toast.error("Error al precargar datos");
    } finally {
      setSeeding(false);
    }
  };

  if (!stats) return <div className="text-muted-foreground">Cargando…</div>;

  const isEmpty = stats.total_cotizaciones === 0 && stats.total_items === 0;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="hidden sm:flex bg-[#2f3d31] rounded-md p-3 shrink-0" data-testid="dashboard-logo">
            <img src={LOGO_URL} alt="Madera Blanca Studio" className="h-20 w-20 object-contain" />
          </div>
          <div>
            <div className="uppercase-label">Panel general</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Bienvenido a Madera Blanca Studio.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Crea, gestiona y presenta cotizaciones de carpintería fina para proyectos residenciales,
              comerciales y de hotelería.
            </p>
          </div>
        </div>
        <Link to="/cotizaciones/nueva">
          <Button
            className="bg-[#8B5A33] hover:bg-[#6D4525] text-white rounded-md px-5"
            data-testid="dashboard-cta-new-quote"
          >
            Nueva cotización <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </header>

      {isEmpty && (
        <div className="border border-dashed border-[#8B5A33]/40 bg-[#8B5A33]/5 rounded-lg p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-start gap-3">
            <Sprout className="w-5 h-5 text-[#8B5A33] mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="font-display text-xl">Empieza con datos de muestra</div>
              <div className="text-sm text-muted-foreground">
                Cargamos las 3 partidas del presupuesto de referencia: cocina CO-01, cubierta de
                cuarzo y mueble de TV MTV-01.
              </div>
            </div>
          </div>
          <Button
            onClick={handleSeed}
            disabled={seeding}
            className="bg-[#2C2A28] hover:bg-[#1c1b1a] text-white"
            data-testid="dashboard-seed-button"
          >
            {seeding ? "Cargando…" : "Precargar datos"}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <StatBlock
          label="Cotizaciones"
          value={stats.total_cotizaciones}
          sub={`${stats.aprobadas} aprobadas`}
          icon={FileText}
          testid="stat-total-quotes"
        />
        <StatBlock
          label="Monto cotizado"
          value={formatMXN(stats.monto_total)}
          sub="MXN total emitido"
          icon={CheckCircle2}
          testid="stat-monto-total"
        />
        <StatBlock
          label="Clientes"
          value={stats.total_clientes}
          icon={Users}
          testid="stat-clientes"
        />
        <StatBlock
          label="Catálogo"
          value={stats.total_items}
          sub="partidas activas"
          icon={BookOpen}
          testid="stat-items"
        />
      </div>

      {/* Gráficas */}
      {(() => {
        const statusData = (stats.por_status || []).filter((s) => s._id).map((s) => ({ name: s._id, value: s.count }));
        const flowData = [
          { name: "Ingresos", value: Number(stats.total_ingresos) || 0 },
          { name: "Egresos", value: Number(stats.total_egresos) || 0 },
        ].filter((x) => x.value > 0);
        const statusColors = {
          "Borrador": "#7A756D",
          "Enviada": "#8B5A33",
          "En proceso": "#D4A373",
          "Aprobada": "#4A7C59",
          "Rechazada": "#BC4749",
        };
        const flowColors = ["#4A7C59", "#BC4749"];
        // Cuentas por cobrar pie: cobrado vs pendiente
        const cxcData = [
          { name: "Cobrado", value: Number(cxc.total_cobrado) || 0 },
          { name: "Pendiente", value: Number(cxc.total_pendiente) || 0 },
        ].filter((x) => x.value > 0);
        const cxcColors = ["#4A7C59", "#D4A373"];
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dashboard-charts">
            <div className="border border-border bg-white rounded-lg p-5">
              <div className="uppercase-label mb-1">Estado de cotizaciones</div>
              <h3 className="font-display text-xl mb-3">Pipeline comercial</h3>
              {statusData.length === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} label={(e) => `${((e.percent || 0) * 100).toFixed(0)}%`} labelLine={false}>
                      {statusData.map((entry) => <Cell key={entry.name} fill={statusColors[entry.name] || "#8B5A33"} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} cot.`} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="border border-border bg-white rounded-lg p-5">
              <div className="uppercase-label mb-1">Flujo de dinero</div>
              <h3 className="font-display text-xl mb-3">Ingresos vs egresos</h3>
              {flowData.length === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">Sin movimientos aún.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={flowData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} label={(e) => `${((e.percent || 0) * 100).toFixed(0)}%`} labelLine={false}>
                      {flowData.map((entry, i) => <Cell key={entry.name} fill={flowColors[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatMXN(v)} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="flex flex-wrap justify-around text-xs border-t border-border pt-2 mt-1">
                <div><span className="text-[#3a6347]">↑</span> {formatMXN(stats.total_ingresos || 0)}</div>
                <div><span className="text-[#8a3537]">↓</span> {formatMXN(stats.total_egresos || 0)}</div>
                <div className={Number(stats.balance || 0) >= 0 ? "text-[#3a6347]" : "text-[#8a3537]"}>= {formatMXN(stats.balance || 0)}</div>
              </div>
            </div>

            <div className="border border-border bg-white rounded-lg p-5" data-testid="chart-cxc">
              <div className="uppercase-label mb-1">Cuentas por cobrar</div>
              <h3 className="font-display text-xl mb-3">Anticipos y saldos</h3>
              {cxcData.length === 0 ? (
                <div className="py-14 text-center text-muted-foreground text-sm">Sin cotizaciones activas.</div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={cxcData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2} label={(e) => `${((e.percent || 0) * 100).toFixed(0)}%`} labelLine={false}>
                        {cxcData.map((entry, i) => <Cell key={entry.name} fill={cxcColors[i]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => formatMXN(v)} />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: "11px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-around text-xs border-t border-border pt-2 mt-1">
                    <div className="text-[#3a6347]">Cobrado: {formatMXN(cxc.total_cobrado)}</div>
                    <div className="text-[#8B5A33]">Pendiente: {formatMXN(cxc.total_pendiente)}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {cxc.items.length > 0 && (
        <section data-testid="cxc-detail">
          <h2 className="font-display text-3xl mb-3">Detalle cuentas por cobrar</h2>
          <div className="border border-border bg-white rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F2EC]/70 text-left">
                <tr>
                  <th className="px-4 py-2 uppercase-label">Folio</th>
                  <th className="px-4 py-2 uppercase-label">Cliente</th>
                  <th className="px-4 py-2 uppercase-label">Proyecto</th>
                  <th className="px-4 py-2 uppercase-label">Estado</th>
                  <th className="px-4 py-2 uppercase-label text-right">Total</th>
                  <th className="px-4 py-2 uppercase-label text-right">Cobrado</th>
                  <th className="px-4 py-2 uppercase-label text-right">Pendiente</th>
                  <th className="px-4 py-2 uppercase-label w-48">Progreso</th>
                </tr>
              </thead>
              <tbody>
                {cxc.items.map((it) => {
                  const pct = Math.max(0, Math.min(100, Number(it.porcentaje_pagado) || 0));
                  const barColor = pct >= 100 ? "#3a6347" : pct >= 50 ? "#8B5A33" : "#BC4749";
                  return (
                  <tr key={it.cotizacion_id} className="border-t border-border" data-testid={`cxc-row-${it.folio}`}>
                    <td className="px-4 py-2 tabular"><Link to={`/cotizaciones/${it.cotizacion_id}`} className="text-[#8B5A33] hover:underline">{it.folio}</Link></td>
                    <td className="px-4 py-2">{it.cliente_nombre}</td>
                    <td className="px-4 py-2 text-muted-foreground max-w-xs truncate">{it.proyecto}</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-1 rounded border ${statusColor(it.status)}`}>{it.status}</span></td>
                    <td className="px-4 py-2 text-right tabular">{formatMXN(it.total)}</td>
                    <td className="px-4 py-2 text-right tabular text-[#3a6347]">{formatMXN(it.cobrado)}</td>
                    <td className="px-4 py-2 text-right tabular font-medium text-[#8B5A33]">{formatMXN(it.pendiente)}</td>
                    <td className="px-4 py-2" data-testid={`cxc-progress-${it.folio}`}>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-[#F4F2EC] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                        <span className="text-xs tabular text-muted-foreground w-12 text-right">{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
